import { useState, useEffect } from 'react';
import { X, Calendar, Repeat, Bell, CalendarDays, Plus, Trash2, Sparkles, Loader2, Wand2 } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../ui/Select';
import { Checkbox } from '../ui/Checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Alert } from '../ui/Alert';
import { NativeSelect } from '../ui/NativeSelect';
import { 
  ListSchedule, 
  ScheduleType, 
  DayOfWeek, 
  RecurringPattern,
  ScheduleNotification 
} from '@/types/calendar';
import { calendarService } from '@/services/calendarService';
import { useListsStore } from '@/store/listsStore';
import { aiService } from '@/services/aiService';
import { personaService } from '@/services/personaService';
import { useGoalsStore } from '@/store/goalsStore';
import { universalCompletionService } from '@/services/universalCompletionService';

interface ScheduleCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onScheduleCreated: (schedule: ListSchedule) => void;
  initialListId?: string;
  initialDate?: Date;
  initialSchedule?: ListSchedule;
}

export function ScheduleCreationModal({
  isOpen,
  onClose,
  onScheduleCreated,
  initialListId,
  initialDate,
  initialSchedule
}: ScheduleCreationModalProps) {
  const { lists } = useListsStore();
  const { getGoalById } = useGoalsStore();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<Partial<ListSchedule>>({
    listId: initialListId || '',
    type: 'one_time',
    isActive: true,
    notifications: []
  });

  const [recurringData, setRecurringData] = useState<Partial<RecurringPattern>>({
    startDate: initialDate || new Date(),
    time: '09:00'
  });

  // AI assistant state
  const [aiSuggestion, setAiSuggestion] = useState<any>(null);
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  useEffect(() => {
    if (initialSchedule) {
      // Editing existing schedule
      setFormData(initialSchedule);
      if (initialSchedule.recurringPattern) {
        setRecurringData(initialSchedule.recurringPattern);
      }
    } else {
      // Creating new schedule
      if (initialListId) {
        setFormData(prev => ({ ...prev, listId: initialListId }));
      }
      if (initialDate) {
        setFormData(prev => ({ ...prev, scheduledDate: initialDate }));
        setRecurringData(prev => ({ ...prev, startDate: initialDate }));
      }
    }
  }, [initialListId, initialDate, initialSchedule]);

  const activeLists = lists.filter(list =>
    list.status !== 'complete'
  );

  const selectedList = lists.find(list => list.id === formData.listId);

  const handleCreateSchedule = async () => {
    if (!formData.listId || !formData.type) return;

    try {
      const scheduleData: any = {
        ...formData as Required<Pick<ListSchedule, 'listId' | 'type' | 'isActive'>>,
        title: formData.title || selectedList?.title,
        description: formData.description || `Scheduled: ${selectedList?.title}`,
        notifications: formData.notifications || []
      };

      if (formData.type === 'one_time') {
        scheduleData.scheduledDate = formData.scheduledDate;
        scheduleData.scheduledTime = formData.scheduledTime;
      } else {
        scheduleData.recurringPattern = recurringData as RecurringPattern;
      }

      let schedule;
      if (initialSchedule) {
        // Update existing schedule
        schedule = calendarService.updateSchedule(initialSchedule.id, scheduleData);
      } else {
        // Create new schedule
        schedule = calendarService.createSchedule(scheduleData);
      }

      onScheduleCreated(schedule);
      onClose();
      resetForm();
    } catch (error) {
      console.error('Failed to save schedule:', error);
    }
  };

  const resetForm = () => {
    setStep(1);
    setFormData({
      listId: '',
      type: 'one_time',
      isActive: true,
      notifications: []
    });
    setRecurringData({
      startDate: new Date(),
      time: '09:00'
    });
    setAiSuggestion(null);
    setAiError(null);
  };

  // AI handler function
  const handleAskAI = async () => {
    if (!formData.listId) {
      setAiError('Please select a list first');
      return;
    }

    const selectedList = lists.find(l => l.id === formData.listId);
    if (!selectedList) {
      setAiError('Selected list not found');
      return;
    }

    if (!aiService.isConfigured()) {
      setAiError('AI service not configured. Please add your API key in Settings.');
      return;
    }

    setIsAiLoading(true);
    setAiError(null);
    
    try {
      // Get user context
      const profile = personaService.getProfile();
      const schedules = calendarService.getAllSchedules();
      
      // Find linked goal if exists
      const goal = selectedList.goalId ? 
        getGoalById(selectedList.goalId) : undefined;
      
      // Default working hours - in a real app this should come from user preferences
      const DEFAULT_WORKING_HOURS = {
        monday: { start: '09:00', end: '17:00', enabled: true },
        tuesday: { start: '09:00', end: '17:00', enabled: true },
        wednesday: { start: '09:00', end: '17:00', enabled: true },
        thursday: { start: '09:00', end: '17:00', enabled: true },
        friday: { start: '09:00', end: '17:00', enabled: true },
        saturday: { start: '10:00', end: '16:00', enabled: false },
        sunday: { start: '10:00', end: '16:00', enabled: false }
      };
      
      // Get current completion context for items
      const contextDate = new Date();
      const listData = universalCompletionService.getListDataForContext(selectedList, contextDate);
      
      // Build schedule request
      const scheduleRequest = {
        userInput: 'Find the optimal time to schedule this list based on my availability and priorities',
        listToSchedule: {
          id: selectedList.id,
          title: selectedList.title,
          description: selectedList.description,
          priority: selectedList.priority,
          estimatedHours: selectedList.estimatedHours,
          deadline: selectedList.deadline?.toISOString(),
          items: listData.items.map(item => ({
            content: item.content,
            estimatedMinutes: item.estimatedMinutes,
            completed: item.completed
          }))
        },
        linkedGoal: goal ? {
          id: goal.id,
          title: goal.title,
          smartCriteria: goal.smartCriteria
        } : undefined,
        existingSchedules: schedules
          .filter(s => s.isActive)
          .map(s => ({
            listId: s.listId,
            listTitle: lists.find(l => l.id === s.listId)?.title || '',
            scheduledDate: s.scheduledDate?.toISOString() || '',
            scheduledTime: s.scheduledTime || '',
            duration: 60, // Default duration
            isRecurring: s.type !== 'one_time'
          })),
        userContext: {
          workingHours: DEFAULT_WORKING_HOURS,
          preferredTimes: profile.preferredWorkStyles,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          currentDate: new Date().toISOString()
        }
      };
      
      const suggestion = await aiService.generateScheduleSuggestion(scheduleRequest);
      setAiSuggestion(suggestion);
      
      // Apply primary suggestion to form with complete auto-filling
      if (suggestion.primaryTimeSlot) {
        // Generate AI description from reasoning
        const aiDescription = `AI scheduled for optimal productivity: ${suggestion.reasoning.slice(0, 3).join(' ')}`;
        
        setFormData(prev => ({
          ...prev,
          title: prev.title || `${selectedList.title} - AI Scheduled`,
          description: prev.description || aiDescription,
          scheduledDate: new Date(suggestion.primaryTimeSlot.date),
          scheduledTime: suggestion.primaryTimeSlot.time,
          type: 'one_time' // Default to one-time for simplicity
        }));
        
        // If AI suggests recurring pattern, apply it
        if (suggestion.recurringPattern) {
          setFormData(prev => ({
            ...prev,
            type: suggestion.recurringPattern!.type as any
          }));
          setRecurringData(prev => ({
            ...prev,
            daysOfWeek: suggestion.recurringPattern!.daysOfWeek as any[],
            dailyInterval: suggestion.recurringPattern!.interval
          }));
        }
      }
      
    } catch (err) {
      setAiError(err instanceof Error ? err.message : 'Failed to generate suggestion');
    } finally {
      setIsAiLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return <ListSelectionStep />;
      case 2:
        return <ScheduleTypeStep />;
      case 3:
        return <ScheduleDetailsStep />;
      case 4:
        return <NotificationsStep />;
      default:
        return null;
    }
  };

  const ListSelectionStep = () => (
    <div className="stack-loose">
      <div>
        <label className="text-label mb-2 block">Select List to Schedule</label>
        <Select
          value={formData.listId}
          onValueChange={(value: string) => setFormData(prev => ({ ...prev, listId: value }))}
          placeholder="Choose a list..."
          searchable
          defaultLabel={selectedList ? `${selectedList.title} (${selectedList.status})` : undefined}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Choose a list..." />
          </SelectTrigger>
          <SelectContent>
            {activeLists.map(list => (
              <SelectItem
                key={list.id}
                value={list.id}
                searchText={`${list.title} ${list.description} ${list.status}`}
              >
                {list.title} ({list.status})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedList && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{selectedList.title}</CardTitle>
            <CardDescription>{selectedList.description}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <Badge variant="outline">{selectedList.status}</Badge>
                <span className="ml-2 text-sm text-muted-foreground">
                  {selectedList.items.length} items
                </span>
              </div>
              <div className="text-sm text-muted-foreground">
                Priority: {selectedList.priority}
              </div>
            </div>
            {selectedList.estimatedHours && (
              <div className="mt-2 text-sm text-muted-foreground">
                Estimated: {selectedList.estimatedHours} hours
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="stack-normal">
        <label htmlFor="schedule-title" className="text-label">Custom Title (Optional)</label>
        <Input
          id="schedule-title"
          name="title"
          aria-label="Schedule title"
          value={formData.title || ''}
          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
          placeholder={selectedList ? `Schedule: ${selectedList.title}` : 'Enter schedule title'}
        />
      </div>

      <div className="stack-normal">
        <label htmlFor="schedule-description" className="text-label">Description (Optional)</label>
        <Input
          id="schedule-description"
          name="description"
          aria-label="Schedule description"
          value={formData.description || ''}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Add notes about this schedule..."
        />
      </div>

      {/* AI Assistant Section - Show only when a list is selected */}
      {selectedList && aiService.isConfigured() && (
        <Card className="border-[rgb(var(--brand-primary-200))]">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-[rgb(var(--brand-primary-600))]" />
              AI Scheduling Assistant
            </CardTitle>
            <CardDescription className="text-xs">
              Let AI automatically schedule "{selectedList.title}" at the optimal time
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              type="button"
              onClick={handleAskAI}
              disabled={isAiLoading || !formData.listId}
              className="w-full gap-2"
              size="sm"
            >
              {isAiLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Analyzing & scheduling...
                </>
              ) : (
                <>
                  <Wand2 className="h-4 w-4" />
                  Auto-Schedule with AI
                </>
              )}
            </Button>
            
            {aiError && (
              <Alert variant="destructive" className="text-xs">
                {aiError}
              </Alert>
            )}
            
            {aiSuggestion && (
              <div className="stack-relaxed">
                <div className="p-3 bg-[rgb(var(--brand-surface-primary))] rounded-md border border-[rgb(var(--brand-success-200))]">
                  <p className="text-sm font-medium text-[rgb(var(--brand-success-700))] mb-2">
                    ✅ AI has scheduled your list!
                  </p>
                  <div className="stack-normal">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Date & Time:</span>
                      <span className="text-sm">
                        {new Date(aiSuggestion.primaryTimeSlot.date).toLocaleDateString()}
                        {' at '}
                        {aiSuggestion.primaryTimeSlot.time}
                      </span>
                    </div>
                    <div className="text-xs text-[rgb(var(--brand-success-600))] stack-tight">
                      {aiSuggestion.reasoning.slice(0, 2).map((reason: string, idx: number) => (
                        <p key={idx}>• {reason}</p>
                      ))}
                    </div>
                  </div>
                </div>
                
                {aiSuggestion.alternativeTimeSlots?.length > 0 && (
                  <div className="stack-normal">
                    <p className="text-xs font-medium text-[rgb(var(--brand-text-secondary))]">
                      Or choose an alternative time:
                    </p>
                    {aiSuggestion.alternativeTimeSlots.map((alt: any, idx: number) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            scheduledDate: new Date(alt.date),
                            scheduledTime: alt.time,
                            description: `AI scheduled: ${alt.reason} (Alternative choice ${idx + 1})`
                          }));
                        }}
                        className="w-full text-left p-2 text-xs bg-[rgb(var(--brand-surface-primary))]
                                 hover:bg-[rgb(var(--brand-surface-secondary))] rounded border border-[rgb(var(--brand-border-primary))]"
                      >
                        <span className="font-medium">
                          {new Date(alt.date).toLocaleDateString()} at {alt.time}
                        </span>
                        <span className="text-[rgb(var(--brand-text-tertiary))] block">
                          {alt.reason}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                
                {aiSuggestion.warnings?.length > 0 && (
                  <Alert variant="warning" className="text-xs">
                    {aiSuggestion.warnings[0]}
                  </Alert>
                )}

                <div className="flex gap-2">
                  <Button
                    type="button"
                    onClick={handleCreateSchedule}
                    size="sm"
                    className="flex-1"
                  >
                    Create Schedule
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setStep(2)}
                    variant="outline"
                    size="sm"
                    className="flex-1"
                  >
                    Customize
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );

  const ScheduleTypeStep = () => (
    <div className="stack-loose">
      <div>
        <label className="text-label mb-2 block">Schedule Type</label>
        <div className="grid grid-cols-2 gap-3">
          {[
            { value: 'one_time', label: 'One-time', icon: Calendar, description: 'Single occurrence' },
            { value: 'daily', label: 'Daily', icon: Repeat, description: 'Every day or weekdays' },
            { value: 'weekly', label: 'Weekly', icon: Repeat, description: 'Specific days of week' },
            { value: 'monthly', label: 'Monthly', icon: Repeat, description: 'Same day each month' },
            { value: 'custom', label: 'Custom Dates', icon: CalendarDays, description: 'Select specific dates' }
          ].map(({ value, label, icon: Icon, description }) => (
            <Card
              key={value}
              className={`cursor-pointer transition-all ${
                formData.type === value
                  ? 'border-[rgb(var(--brand-primary-500))] bg-[rgba(var(--brand-primary-500),0.05)]'
                  : 'hover:border-[rgb(var(--brand-border-secondary))]'
              }`}
              onClick={() => setFormData(prev => ({ ...prev, type: value as ScheduleType }))}
            >
              <CardContent className="p-4 text-center">
                <Icon className="h-8 w-8 mx-auto mb-2 text-[rgb(var(--brand-text-secondary))]" />
                <div className="font-medium">{label}</div>
                <div className="text-xs text-muted-foreground mt-1">{description}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );

  const ScheduleDetailsStep = () => (
    <div className="stack-loose">
      {formData.type === 'one_time' ? (
        <>
          <div className="stack-normal">
            <label htmlFor="schedule-date" className="text-label">Date</label>
            <Input
              id="schedule-date"
              name="date"
              aria-label="Schedule date"
              type="date"
              value={formData.scheduledDate?.toISOString().split('T')[0] || ''}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                scheduledDate: new Date(e.target.value)
              }))}
            />
          </div>
          <div className="stack-normal">
            <label htmlFor="schedule-time" className="text-label">Time</label>
            <Input
              id="schedule-time"
              name="time"
              aria-label="Schedule time"
              type="time"
              value={formData.scheduledTime || '09:00'}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                scheduledTime: e.target.value
              }))}
            />
          </div>
        </>
      ) : (
        <RecurringPatternForm />
      )}

    </div>
  );

  const RecurringPatternForm = () => {
    const daysOfWeek: { value: DayOfWeek; label: string }[] = [
      { value: 'monday', label: 'Mon' },
      { value: 'tuesday', label: 'Tue' },
      { value: 'wednesday', label: 'Wed' },
      { value: 'thursday', label: 'Thu' },
      { value: 'friday', label: 'Fri' },
      { value: 'saturday', label: 'Sat' },
      { value: 'sunday', label: 'Sun' }
    ];

    const addCustomDate = () => {
      const newDate = new Date();
      setRecurringData(prev => ({
        ...prev,
        customDates: [...(prev.customDates || []), newDate]
      }));
    };

    const removeCustomDate = (index: number) => {
      setRecurringData(prev => ({
        ...prev,
        customDates: prev.customDates?.filter((_, i) => i !== index)
      }));
    };

    const updateCustomDate = (index: number, date: Date) => {
      setRecurringData(prev => ({
        ...prev,
        customDates: prev.customDates?.map((d, i) => i === index ? date : d)
      }));
    };

    return (
      <div className="stack-loose">
        <div className="grid grid-cols-2 gap-4">
          <div className="stack-normal">
            <label htmlFor="recurring-start-date" className="text-label">Start Date</label>
            <Input
              id="recurring-start-date"
              name="startDate"
              aria-label="Recurring schedule start date"
              type="date"
              value={recurringData.startDate?.toISOString().split('T')[0] || ''}
              onChange={(e) => setRecurringData(prev => ({
                ...prev,
                startDate: new Date(e.target.value)
              }))}
            />
          </div>
          <div className="stack-normal">
            <label htmlFor="recurring-time" className="text-label">Time</label>
            <Input
              id="recurring-time"
              name="time"
              aria-label="Recurring schedule time"
              type="time"
              value={recurringData.time || '09:00'}
              onChange={(e) => setRecurringData(prev => ({
                ...prev,
                time: e.target.value
              }))}
            />
          </div>
        </div>

        {formData.type === 'daily' && (
          <>
            <div className="stack-normal">
              <label className="text-label">Repeat every</label>
              <div className="flex items-center space-x-2">
                <Input
                  type="number"
                  min="1"
                  max="30"
                  value={recurringData.dailyInterval || 1}
                  onChange={(e) => setRecurringData(prev => ({ 
                    ...prev, 
                    dailyInterval: parseInt(e.target.value) 
                  }))}
                  className="w-20"
                />
                <span className="text-sm">days</span>
              </div>
            </div>
            <div className="stack-normal">
              <label className="text-label">Days of week (optional)</label>
              <div className="flex flex-wrap gap-2">
                {daysOfWeek.map(({ value, label }) => (
                  <label key={value} className="flex items-center gap-1">
                    <Checkbox
                      checked={recurringData.daysOfWeek?.includes(value) || false}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setRecurringData(prev => ({ 
                            ...prev, 
                            daysOfWeek: [...(prev.daysOfWeek || []), value] 
                          }));
                        } else {
                          setRecurringData(prev => ({ 
                            ...prev, 
                            daysOfWeek: prev.daysOfWeek?.filter(d => d !== value) 
                          }));
                        }
                      }}
                    />
                    <span className="text-sm">{label}</span>
                  </label>
                ))}
              </div>
            </div>
          </>
        )}

        {formData.type === 'weekly' && (
          <>
            <div className="stack-normal">
              <label className="text-label">Repeat every</label>
              <div className="flex items-center space-x-2">
                <Input
                  type="number"
                  min="1"
                  max="52"
                  value={recurringData.weeklyInterval || 1}
                  onChange={(e) => setRecurringData(prev => ({ 
                    ...prev, 
                    weeklyInterval: parseInt(e.target.value) 
                  }))}
                  className="w-20"
                />
                <span className="text-sm">weeks</span>
              </div>
            </div>
            <div className="stack-normal">
              <label className="text-label">On these days</label>
              <div className="flex flex-wrap gap-2">
                {daysOfWeek.map(({ value, label }) => (
                  <label key={value} className="flex items-center gap-1">
                    <Checkbox
                      checked={recurringData.daysOfWeek?.includes(value) || false}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setRecurringData(prev => ({ 
                            ...prev, 
                            daysOfWeek: [...(prev.daysOfWeek || []), value] 
                          }));
                        } else {
                          setRecurringData(prev => ({ 
                            ...prev, 
                            daysOfWeek: prev.daysOfWeek?.filter(d => d !== value) 
                          }));
                        }
                      }}
                    />
                    <span className="text-sm">{label}</span>
                  </label>
                ))}
              </div>
            </div>
          </>
        )}

        {formData.type === 'monthly' && (
          <div className="stack-loose">
            <div className="stack-normal">
              <label className="text-label">Repeat every</label>
              <div className="flex items-center space-x-2">
                <Input
                  type="number"
                  min="1"
                  max="12"
                  value={recurringData.monthlyInterval || 1}
                  onChange={(e) => setRecurringData(prev => ({ 
                    ...prev, 
                    monthlyInterval: parseInt(e.target.value) 
                  }))}
                  className="w-20"
                />
                <span className="text-sm">months</span>
              </div>
            </div>
            
            <div className="stack-relaxed">
              <label className="text-label">On</label>
              <div className="stack-normal">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="monthlyType"
                    checked={!recurringData.weekOfMonth}
                    onChange={() => setRecurringData(prev => ({ 
                      ...prev, 
                      weekOfMonth: undefined,
                      dayOfWeekInMonth: undefined 
                    }))}
                  />
                  <span className="text-sm">Specific day of month:</span>
                  <Input
                    type="number"
                    min="1"
                    max="31"
                    value={recurringData.dayOfMonth || 1}
                    onChange={(e) => setRecurringData(prev => ({ 
                      ...prev, 
                      dayOfMonth: parseInt(e.target.value) 
                    }))}
                    className="w-20"
                    disabled={!!recurringData.weekOfMonth}
                  />
                </label>
                
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="monthlyType"
                    checked={!!recurringData.weekOfMonth}
                    onChange={() => setRecurringData(prev => ({ 
                      ...prev, 
                      weekOfMonth: 1,
                      dayOfWeekInMonth: 'monday',
                      dayOfMonth: undefined 
                    }))}
                  />
                  <span className="text-sm">Specific week and day:</span>
                </label>
                
                {recurringData.weekOfMonth && (
                  <div className="ml-6 flex items-center space-x-2">
                    <NativeSelect
                      value={recurringData.weekOfMonth}
                      onChange={(e) => setRecurringData(prev => ({
                        ...prev,
                        weekOfMonth: parseInt(e.target.value)
                      }))}
                    >
                      <option value={1}>First</option>
                      <option value={2}>Second</option>
                      <option value={3}>Third</option>
                      <option value={4}>Fourth</option>
                      <option value={-1}>Last</option>
                    </NativeSelect>
                    <NativeSelect
                      value={recurringData.dayOfWeekInMonth}
                      onChange={(e) => setRecurringData(prev => ({
                        ...prev,
                        dayOfWeekInMonth: e.target.value as DayOfWeek
                      }))}
                    >
                      {daysOfWeek.map(({ value, label }) => (
                        <option key={value} value={value}>{label}day</option>
                      ))}
                    </NativeSelect>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {formData.type === 'custom' && (
          <div className="stack-loose">
            <div className="stack-normal">
              <div className="flex items-center justify-between">
                <label className="text-label">Custom Dates</label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addCustomDate}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Date
                </Button>
              </div>

              {(!recurringData.customDates || recurringData.customDates.length === 0) && (
                <div className="text-center py-8 text-muted-foreground border-2 border-dashed border-[rgb(var(--brand-border-primary))] rounded-lg">
                  <CalendarDays className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No dates selected</p>
                  <p className="text-sm">Click "Add Date" to select specific dates</p>
                </div>
              )}

              {recurringData.customDates && recurringData.customDates.length > 0 && (
                <div className="stack-normal max-h-48 overflow-y-auto">
                  {recurringData.customDates.map((date, index) => (
                    <div key={index} className="flex items-center space-x-2 p-2 border border-[rgb(var(--brand-border-primary))] rounded">
                      <Input
                        type="date"
                        value={date.toISOString().split('T')[0]}
                        onChange={(e) => updateCustomDate(index, new Date(e.target.value))}
                        className="flex-1"
                      />
                      <Input
                        type="time"
                        value={recurringData.time || '09:00'}
                        onChange={(e) => setRecurringData(prev => ({
                          ...prev,
                          time: e.target.value
                        }))}
                        className="w-24"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeCustomDate(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="stack-normal">
              <label className="text-label">Default Time for All Dates</label>
              <Input
                type="time"
                value={recurringData.time || '09:00'}
                onChange={(e) => setRecurringData(prev => ({
                  ...prev,
                  time: e.target.value
                }))}
                className="w-32"
              />
              <p className="text-xs text-muted-foreground">
                This time will be applied to all custom dates (can be overridden per date above)
              </p>
            </div>
          </div>
        )}

        <div className="stack-normal">
          <label className="text-label">End date (optional)</label>
          <Input
            type="date"
            value={recurringData.endDate?.toISOString().split('T')[0] || ''}
            onChange={(e) => setRecurringData(prev => ({
              ...prev,
              endDate: e.target.value ? new Date(e.target.value) : undefined
            }))}
          />
        </div>

        <div className="stack-normal">
          <label className="text-label">Max occurrences (optional)</label>
          <Input
            type="number"
            min="1"
            value={recurringData.maxOccurrences || ''}
            onChange={(e) => setRecurringData(prev => ({
              ...prev,
              maxOccurrences: e.target.value ? parseInt(e.target.value) : undefined
            }))}
            placeholder="Leave empty for unlimited"
          />
        </div>
      </div>
    );
  };

  const NotificationsStep = () => {
    const addNotification = () => {
      const newNotification: ScheduleNotification = {
        id: `notification-${Date.now()}`,
        type: 'push',
        minutesBefore: 15,
        isEnabled: true
      };
      setFormData(prev => ({ 
        ...prev, 
        notifications: [...(prev.notifications || []), newNotification] 
      }));
    };

    const removeNotification = (notificationId: string) => {
      setFormData(prev => ({ 
        ...prev, 
        notifications: prev.notifications?.filter(n => n.id !== notificationId) 
      }));
    };

    return (
      <div className="stack-loose">
        <div className="flex items-center justify-between">
          <h3 className="text-heading-md">Notifications</h3>
          <Button variant="outline" size="sm" onClick={addNotification}>
            <Bell className="h-4 w-4 mr-1" />
            Add
          </Button>
        </div>

        {formData.notifications?.map((notification, index) => (
          <Card key={notification.id}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <Bell className="h-4 w-4" />
                  <span className="font-medium">Notification {index + 1}</span>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => removeNotification(notification.id)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-label mb-1 block">Type</label>
                  <Select
                    value={notification.type}
                    onValueChange={(value: string) => {
                      const updated = formData.notifications?.map(n =>
                        n.id === notification.id
                          ? { ...n, type: value as 'email' | 'push' | 'in_app' }
                          : n
                      );
                      setFormData(prev => ({ ...prev, notifications: updated }));
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="push">Push Notification</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="in_app">In-App</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-label mb-1 block">Minutes Before</label>
                  <Input
                    type="number"
                    min="0"
                    value={notification.minutesBefore}
                    onChange={(e) => {
                      const updated = formData.notifications?.map(n => 
                        n.id === notification.id 
                          ? { ...n, minutesBefore: parseInt(e.target.value) }
                          : n
                      );
                      setFormData(prev => ({ ...prev, notifications: updated }));
                    }}
                  />
                </div>
              </div>

              <div className="mt-3">
                <label className="text-label mb-1 block">Custom Message (Optional)</label>
                <Input
                  value={notification.message || ''}
                  onChange={(e) => {
                    const updated = formData.notifications?.map(n => 
                      n.id === notification.id 
                        ? { ...n, message: e.target.value }
                        : n
                    );
                    setFormData(prev => ({ ...prev, notifications: updated }));
                  }}
                  placeholder="Custom notification message..."
                />
              </div>
            </CardContent>
          </Card>
        ))}

        {!formData.notifications?.length && (
          <div className="text-center py-8 text-muted-foreground">
            <Bell className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No notifications configured</p>
            <p className="text-sm">Click "Add" to create notification reminders</p>
          </div>
        )}
      </div>
    );
  };

  const canProceedToNext = () => {
    switch (step) {
      case 1:
        return formData.listId;
      case 2:
        return formData.type;
      case 3:
        if (formData.type === 'one_time') {
          return formData.scheduledDate;
        }
        if (formData.type === 'custom') {
          return recurringData.customDates && recurringData.customDates.length > 0 && recurringData.time;
        }
        return recurringData.startDate && recurringData.time;
      case 4:
        return true;
      default:
        return false;
    }
  };

  const getStepTitle = () => {
    switch (step) {
      case 1: return 'Select List';
      case 2: return 'Schedule Type';
      case 3: return 'Schedule Details';
      case 4: return 'Notifications';
      default: return '';
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      scrollBehavior="inside"
      title={initialSchedule ? 'Edit Schedule' : 'Create Schedule'}
      showCloseButton={true}
    >
      {/* Progress indicator - responsive */}
      <div className="mb-6">
        {/* Mobile: Compact dot indicator */}
        <div className="flex sm:hidden items-center justify-center gap-1.5">
          {[1, 2, 3, 4].map((stepNumber) => (
            <div
              key={stepNumber}
              className={`h-2 rounded-full transition-all ${
                stepNumber === step
                  ? 'w-6 bg-[rgb(var(--brand-primary-500))]'
                  : stepNumber < step
                  ? 'w-2 bg-[rgb(var(--brand-primary-500))]'
                  : 'w-2 bg-[rgb(var(--brand-surface-tertiary))]'
              }`}
              aria-label={`Step ${stepNumber}${stepNumber === step ? ' (current)' : ''}`}
            />
          ))}
          <span className="ml-2 text-xs text-[rgb(var(--brand-text-secondary))]">
            {step}/4
          </span>
        </div>

        {/* Desktop: Full stepper with numbers */}
        <div className="hidden sm:flex items-center justify-between w-full">
          {[1, 2, 3, 4].map((stepNumber) => (
            <div key={stepNumber} className="flex items-center flex-1 last:flex-none">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium shrink-0 ${
                  stepNumber <= step
                    ? 'bg-[rgb(var(--brand-primary-500))] text-white'
                    : 'bg-[rgb(var(--brand-surface-tertiary))] text-[rgb(var(--brand-text-tertiary))]'
                }`}
              >
                {stepNumber}
              </div>
              {stepNumber < 4 && (
                <div
                  className={`h-1 mx-2 flex-1 min-w-4 ${
                    stepNumber < step
                      ? 'bg-[rgb(var(--brand-primary-500))]'
                      : 'bg-[rgb(var(--brand-surface-tertiary))]'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
      </div>

        <div className="mb-6">
          <h3 className="text-heading-md mb-4">
            Step {step}: {getStepTitle()}
          </h3>
          {renderStepContent()}
        </div>

        {/* Navigation buttons */}
        <div className="flex justify-between items-center pt-4 border-t border-[rgb(var(--brand-border-primary))]">
          <Button
            variant="outline"
            onClick={() => setStep(step - 1)}
            disabled={step === 1}
          >
            Previous
          </Button>

          <div className="flex gap-3">
            {step < 4 ? (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={!canProceedToNext()}
              >
                Next
              </Button>
            ) : (
              <Button
                onClick={handleCreateSchedule}
                disabled={!canProceedToNext()}
              >
                {initialSchedule ? 'Save Changes' : 'Create Schedule'}
              </Button>
            )}
          </div>
        </div>
    </Modal>
  );
}